/**
 * P1-001: DECISION LEDGER UI
 * ==========================
 * Complete decision history with integrity verification
 * 
 * Vision: The foundation for Decision Integrity, Agent Accountability,
 * Governance Visibility, and Autonomous Delivery Governance.
 * 
 * Features:
 * - Decision History View with comprehensive display
 * - Decision Detail View with full context
 * - Advanced Filters (type, entity, date, status, requestor, approver)
 * - Timeline View showing decision lineage
 * - Integrity Indicators with verification API
 * - Executive View answering key governance questions
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Shield, ShieldCheck, ShieldAlert, ShieldX, Clock, User, Calendar,
  Filter, RefreshCw, Search, ChevronDown, ChevronRight, CheckCircle,
  XCircle, AlertTriangle, FileText, Building, Briefcase, Activity,
  Hash, GitBranch, ArrowRight, ArrowDown, Loader2, Eye, Download,
  BarChart3, TrendingUp, Users, Folder, Bot, Zap, History, 
  ExternalLink, X, Copy, Check
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  const tenantId = localStorage.getItem('impersonated_tenant_id') || localStorage.getItem('simpliview_user_tenant_id');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (tenantId) headers['X-Tenant-ID'] = tenantId;
  return headers;
};

// ==========================================
// CONSTANTS & CONFIGURATIONS
// ==========================================

const DECISION_CATEGORIES = {
  demand_management: { label: 'Demand Management', color: 'blue', icon: Briefcase },
  intent_management: { label: 'Intent/Baseline', color: 'purple', icon: GitBranch },
  project_lifecycle: { label: 'Project Lifecycle', color: 'indigo', icon: Folder },
  governance: { label: 'Governance', color: 'amber', icon: Shield },
  risk_management: { label: 'Risk Management', color: 'red', icon: AlertTriangle },
  health_management: { label: 'Health Management', color: 'green', icon: Activity },
  status_management: { label: 'Status Management', color: 'gray', icon: Clock },
  resource_management: { label: 'Resource Management', color: 'cyan', icon: Users },
  benefits_management: { label: 'Benefits Management', color: 'emerald', icon: TrendingUp },
  agent_governance: { label: 'Agent Governance', color: 'violet', icon: Bot },
  general: { label: 'General', color: 'slate', icon: FileText }
};

const MAKER_TYPES = {
  human: { label: 'Human', icon: User, color: 'blue' },
  system: { label: 'System', icon: Zap, color: 'amber' },
  agent: { label: 'AI Agent', icon: Bot, color: 'purple' }
};

const INTEGRITY_STATUS = {
  verified: { label: 'Verified', icon: ShieldCheck, color: 'green', bg: 'bg-green-50 border-green-200' },
  warning: { label: 'Warning', icon: ShieldAlert, color: 'amber', bg: 'bg-amber-50 border-amber-200' },
  failed: { label: 'Failed', icon: ShieldX, color: 'red', bg: 'bg-red-50 border-red-200' },
  pending: { label: 'Pending', icon: Shield, color: 'gray', bg: 'bg-gray-50 border-gray-200' }
};

// ==========================================
// STATIC TAILWIND CLASS MAPPINGS
// (Tailwind purges dynamic classes at build time - use static maps)
// ==========================================

const COLOR_CLASSES = {
  blue: { bg: 'bg-blue-100', text: 'text-blue-600', textDark: 'text-blue-700' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-600', textDark: 'text-purple-700' },
  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600', textDark: 'text-indigo-700' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-600', textDark: 'text-amber-700' },
  red: { bg: 'bg-red-100', text: 'text-red-600', textDark: 'text-red-700' },
  green: { bg: 'bg-green-100', text: 'text-green-600', textDark: 'text-green-700' },
  gray: { bg: 'bg-gray-100', text: 'text-gray-600', textDark: 'text-gray-700' },
  cyan: { bg: 'bg-cyan-100', text: 'text-cyan-600', textDark: 'text-cyan-700' },
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', textDark: 'text-emerald-700' },
  violet: { bg: 'bg-violet-100', text: 'text-violet-600', textDark: 'text-violet-700' },
  slate: { bg: 'bg-slate-100', text: 'text-slate-600', textDark: 'text-slate-700' }
};

// ==========================================
// UTILITY COMPONENTS
// ==========================================

const IntegrityBadge = ({ status, onClick }) => {
  const config = INTEGRITY_STATUS[status] || INTEGRITY_STATUS.pending;
  const Icon = config.icon;
  const colorClasses = COLOR_CLASSES[config.color] || COLOR_CLASSES.gray;
  
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors hover:shadow-sm ${config.bg} ${colorClasses.textDark}`}
      data-testid={`integrity-badge-${status}`}
    >
      <Icon className="w-3.5 h-3.5" />
      {config.label}
    </button>
  );
};

const MakerBadge = ({ type, name }) => {
  const config = MAKER_TYPES[type] || MAKER_TYPES.human;
  const Icon = config.icon;
  const colorClasses = COLOR_CLASSES[config.color] || COLOR_CLASSES.blue;
  
  return (
    <div className="inline-flex items-center gap-1.5">
      <span className={`p-1 rounded ${colorClasses.bg}`}>
        <Icon className={`w-3.5 h-3.5 ${colorClasses.text}`} />
      </span>
      <span className="text-sm text-gray-700">{name || 'Unknown User'}</span>
    </div>
  );
};

const CategoryBadge = ({ category }) => {
  const config = DECISION_CATEGORIES[category] || DECISION_CATEGORIES.general;
  const Icon = config.icon;
  const colorClasses = COLOR_CLASSES[config.color] || COLOR_CLASSES.slate;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${colorClasses.bg} ${colorClasses.textDark}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

const CopyButton = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success(`${label || 'Text'} copied to clipboard`);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <button
      onClick={handleCopy}
      className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600"
      title={`Copy ${label || 'text'}`}
    >
      {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
    </button>
  );
};

// ==========================================
// STATISTICS CARDS
// ==========================================

const StatCard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
  <div className="bg-white rounded-xl border shadow-sm p-5" data-testid={`stat-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        {trend && (
          <div className={`flex items-center gap-1 mt-2 text-sm ${trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-gray-500'}`}>
            <TrendingUp className="w-4 h-4" />
            <span>{trend > 0 ? '+' : ''}{trend}% vs last period</span>
          </div>
        )}
      </div>
      <div className={`p-3 rounded-xl bg-${color}-100`}>
        <Icon className={`w-6 h-6 text-${color}-600`} />
      </div>
    </div>
  </div>
);

// ==========================================
// EXECUTIVE VIEW COMPONENT
// ==========================================

const ExecutiveView = ({ stats, recentDecisions, onViewDecision }) => {
  const weeklyDecisions = useMemo(() => {
    if (!recentDecisions) return [];
    
    // Group decisions by day for the last 7 days
    const now = new Date();
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const dayKey = date.toISOString().split('T')[0];
      days.push({
        date: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        count: recentDecisions.filter(d => d.timestamp?.startsWith(dayKey)).length
      });
    }
    return days;
  }, [recentDecisions]);

  const scopeChangeDecisions = useMemo(() => {
    if (!recentDecisions) return [];
    return recentDecisions.filter(d => 
      d.decision_type?.includes('scope') || 
      d.decision_type?.includes('baseline')
    ).slice(0, 5);
  }, [recentDecisions]);

  const riskDecisions = useMemo(() => {
    if (!recentDecisions) return [];
    return recentDecisions.filter(d => 
      d.decision_type?.includes('risk') || 
      d.impact_assessment?.risk_impact
    ).slice(0, 5);
  }, [recentDecisions]);

  return (
    <div className="space-y-6" data-testid="executive-view">
      {/* Key Questions Section */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 text-white">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Executive Governance Summary
        </h2>
        
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
            <p className="text-sm text-gray-300">Decisions This Week</p>
            <p className="text-3xl font-bold mt-1">{stats?.total_decisions || 0}</p>
          </div>
          <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
            <p className="text-sm text-gray-300">Human Decisions</p>
            <p className="text-3xl font-bold mt-1">{stats?.by_maker?.human || 0}</p>
          </div>
          <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
            <p className="text-sm text-gray-300">System Automated</p>
            <p className="text-3xl font-bold mt-1">{stats?.by_maker?.system || 0}</p>
          </div>
          <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
            <p className="text-sm text-gray-300">Pending Outcomes</p>
            <p className="text-3xl font-bold mt-1">{stats?.by_outcome?.pending || 0}</p>
          </div>
        </div>
      </div>

      {/* Weekly Trend */}
      <div className="bg-white rounded-xl border shadow-sm p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Decision Activity (Last 7 Days)</h3>
        <ResponsiveContainer width="100%" height={150}>
          <BarChart data={weeklyDecisions}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Bar dataKey="count" fill="#6366F1" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Key Questions Grid */}
      <div className="grid grid-cols-2 gap-6">
        {/* Scope Changes */}
        <div className="bg-white rounded-xl border shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <GitBranch className="w-4 h-4 text-purple-500" />
            Which decisions changed scope?
          </h3>
          {scopeChangeDecisions.length > 0 ? (
            <div className="space-y-2">
              {scopeChangeDecisions.map(d => (
                <button
                  key={d.decision_id}
                  onClick={() => onViewDecision(d)}
                  className="w-full text-left p-3 rounded-lg bg-purple-50 hover:bg-purple-100 transition-colors"
                >
                  <p className="font-medium text-sm text-gray-900 truncate">{d.entity_name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{d.decision_type?.replace(/_/g, ' ')} • {d.made_by_name}</p>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic">No scope changes this period</p>
          )}
        </div>

        {/* Risk Decisions */}
        <div className="bg-white rounded-xl border shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            Which decisions increased risk?
          </h3>
          {riskDecisions.length > 0 ? (
            <div className="space-y-2">
              {riskDecisions.map(d => (
                <button
                  key={d.decision_id}
                  onClick={() => onViewDecision(d)}
                  className="w-full text-left p-3 rounded-lg bg-red-50 hover:bg-red-100 transition-colors"
                >
                  <p className="font-medium text-sm text-gray-900 truncate">{d.entity_name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{d.decision_type?.replace(/_/g, ' ')} • {d.made_by_name}</p>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic">No risk-related decisions this period</p>
          )}
        </div>
      </div>

      {/* Decision Distribution */}
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">By Category</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={Object.entries(stats?.by_category || {})
                  .filter(([name]) => name && name !== 'null' && name !== 'undefined')
                  .map(([name, value]) => ({ 
                    name: DECISION_CATEGORIES[name]?.label || name.replace(/_/g, ' '), 
                    value 
                  }))}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={70}
                paddingAngle={2}
                dataKey="value"
                label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
              >
                {Object.entries(stats?.by_category || {})
                  .filter(([name]) => name && name !== 'null' && name !== 'undefined')
                  .map(([name], index) => {
                    const colors = ['#6366F1', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#06B6D4'];
                    return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                  })}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">By Decision Maker</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={Object.entries(stats?.by_maker || {})
                  .filter(([name]) => name && name !== 'null' && name !== 'undefined')
                  .map(([name, value]) => ({ 
                    name: MAKER_TYPES[name]?.label || name, 
                    value 
                  }))}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={70}
                paddingAngle={2}
                dataKey="value"
              >
                {Object.entries(stats?.by_maker || {})
                  .filter(([name]) => name && name !== 'null' && name !== 'undefined')
                  .map(([name], index) => {
                    const colors = ['#3B82F6', '#F59E0B', '#8B5CF6'];
                    return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                  })}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// TIMELINE VIEW COMPONENT
// ==========================================

const TimelineView = ({ decisions, onViewDecision }) => {
  if (!decisions || decisions.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400">
        <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No decisions to display in timeline</p>
      </div>
    );
  }

  // Group by entity for visual flow
  const groupedByEntity = decisions.reduce((acc, d) => {
    const key = `${d.entity_type}-${d.entity_id}`;
    if (!acc[key]) {
      acc[key] = { entity_type: d.entity_type, entity_id: d.entity_id, entity_name: d.entity_name, decisions: [] };
    }
    acc[key].decisions.push(d);
    return acc;
  }, {});

  return (
    <div className="space-y-8" data-testid="timeline-view">
      {Object.values(groupedByEntity).map((group, idx) => (
        <div key={idx} className="bg-white rounded-xl border shadow-sm overflow-hidden">
          {/* Entity Header */}
          <div className="bg-gradient-to-r from-indigo-500 to-purple-500 px-5 py-3 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide opacity-80">{group.entity_type}</p>
                <p className="font-semibold">{group.entity_name}</p>
              </div>
              <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                {group.decisions.length} decision{group.decisions.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          {/* Timeline */}
          <div className="p-5">
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-4 top-2 bottom-2 w-0.5 bg-gray-200" />

              <div className="space-y-4">
                {group.decisions.map((decision, dIdx) => {
                  const categoryConfig = DECISION_CATEGORIES[decision.decision_category] || DECISION_CATEGORIES.general;
                  const Icon = categoryConfig.icon;
                  // Static color mapping for timeline node
                  const nodeColorMap = {
                    blue: 'bg-blue-500', purple: 'bg-purple-500', indigo: 'bg-indigo-500',
                    amber: 'bg-amber-500', red: 'bg-red-500', green: 'bg-green-500',
                    gray: 'bg-gray-500', cyan: 'bg-cyan-500', emerald: 'bg-emerald-500',
                    violet: 'bg-violet-500', slate: 'bg-slate-500'
                  };
                  const nodeColor = nodeColorMap[categoryConfig.color] || 'bg-indigo-500';

                  return (
                    <div key={decision.decision_id} className="relative pl-12">
                      {/* Timeline node */}
                      <div className={`absolute left-2 w-5 h-5 rounded-full border-2 border-white shadow ${nodeColor} flex items-center justify-center`}>
                        <Icon className="w-3 h-3 text-white" />
                      </div>

                      {/* Content */}
                      <button
                        onClick={() => onViewDecision(decision)}
                        className="w-full text-left p-4 rounded-lg border hover:border-indigo-300 hover:shadow-sm transition-all bg-gray-50 hover:bg-white"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-gray-900">
                              {decision.decision_type?.replace(/_/g, ' ')}
                            </p>
                            <p className="text-sm text-gray-500 mt-1 line-clamp-2">{decision.rationale}</p>
                          </div>
                          <div className="text-right flex-shrink-0 ml-4">
                            <p className="text-xs text-gray-400">
                              {new Date(decision.timestamp).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                            <MakerBadge type={decision.made_by} name={decision.made_by_name} />
                          </div>
                        </div>

                        {/* State transitions */}
                        {decision.state_transitions?.length > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <div className="flex flex-wrap gap-2">
                              {decision.state_transitions.map((t, tIdx) => (
                                <span key={tIdx} className="inline-flex items-center gap-1 text-xs bg-gray-100 px-2 py-1 rounded">
                                  <span className="text-gray-500">{t.field_name}:</span>
                                  <span className="text-red-500 line-through">{String(t.previous_value)}</span>
                                  <ArrowRight className="w-3 h-3 text-gray-400" />
                                  <span className="text-green-600 font-medium">{String(t.new_value)}</span>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </button>

                      {/* Arrow to next */}
                      {dIdx < group.decisions.length - 1 && (
                        <div className="absolute left-[18px] -bottom-2 text-gray-300">
                          <ArrowDown className="w-3 h-3" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ==========================================
// DECISION DETAIL DRAWER
// ==========================================

const DecisionDetailDrawer = ({ decision, isOpen, onClose }) => {
  const [integrityResult, setIntegrityResult] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const verifyIntegrity = async () => {
    if (!decision) return;
    setVerifying(true);
    try {
      const res = await fetch(`${API_BASE}/decision-ledger/${decision.decision_id}/verify`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setIntegrityResult(data);
      if (data.valid) {
        toast.success('Decision integrity verified');
      } else {
        toast.error('Integrity check failed - possible tampering detected');
      }
    } catch (err) {
      toast.error('Failed to verify integrity');
    } finally {
      setVerifying(false);
    }
  };

  if (!isOpen || !decision) return null;

  const categoryConfig = DECISION_CATEGORIES[decision.decision_category] || DECISION_CATEGORIES.general;
  const CategoryIcon = categoryConfig.icon;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="decision-detail-drawer">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-2xl bg-white shadow-2xl flex flex-col animate-in slide-in-from-right">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-start justify-between z-10">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <CategoryBadge category={decision.decision_category} />
              <span className="text-xs text-gray-400 font-mono">{decision.decision_id}</span>
              <CopyButton text={decision.decision_id} label="Decision ID" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mt-2">
              {decision.decision_type?.replace(/_/g, ' ')}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {decision.entity_type} • {decision.entity_name}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Integrity Section */}
          <div className={`rounded-xl p-4 border ${integrityResult ? (integrityResult.valid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200') : 'bg-gray-50 border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {integrityResult ? (
                  integrityResult.valid ? (
                    <ShieldCheck className="w-8 h-8 text-green-500" />
                  ) : (
                    <ShieldX className="w-8 h-8 text-red-500" />
                  )
                ) : (
                  <Shield className="w-8 h-8 text-gray-400" />
                )}
                <div>
                  <p className="font-semibold text-gray-900">Decision Integrity</p>
                  <p className="text-sm text-gray-500">
                    {integrityResult 
                      ? integrityResult.reason 
                      : 'Click to verify this decision has not been tampered with'}
                  </p>
                </div>
              </div>
              <button
                onClick={verifyIntegrity}
                disabled={verifying}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                data-testid="verify-integrity-btn"
              >
                {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                Verify
              </button>
            </div>
            
            {integrityResult && (
              <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-2 gap-4 text-xs font-mono">
                <div>
                  <p className="text-gray-500 mb-1">Stored Hash</p>
                  <p className="bg-white rounded p-2 border truncate">{integrityResult.stored_hash}</p>
                </div>
                <div>
                  <p className="text-gray-500 mb-1">Calculated Hash</p>
                  <p className="bg-white rounded p-2 border truncate">{integrityResult.calculated_hash}</p>
                </div>
              </div>
            )}
          </div>

          {/* Decision Context */}
          <div className="bg-white rounded-xl border p-5">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-400" />
              Decision Context
            </h3>
            
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Rationale</p>
                <p className="text-gray-900 mt-1">{decision.rationale}</p>
              </div>

              {decision.context && Object.keys(decision.context).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Context Snapshot</p>
                  <pre className="mt-1 bg-gray-50 rounded-lg p-3 text-xs overflow-auto max-h-40">
                    {JSON.stringify(decision.context, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>

          {/* Who & When */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Decision Maker</p>
              <MakerBadge type={decision.made_by} name={decision.made_by_name} />
              <p className="text-xs text-gray-400 mt-2">
                Authority: {decision.authority_level?.replace(/_/g, ' ')}
              </p>
            </div>
            
            <div className="bg-white rounded-xl border p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Timestamp</p>
              <p className="font-medium text-gray-900">
                {new Date(decision.timestamp).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
              <p className="text-sm text-gray-500">
                {new Date(decision.timestamp).toLocaleTimeString('en-US')}
              </p>
            </div>
          </div>

          {/* State Transitions */}
          {decision.state_transitions?.length > 0 && (
            <div className="bg-white rounded-xl border p-5">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <GitBranch className="w-4 h-4 text-gray-400" />
                State Transitions
              </h3>
              <div className="space-y-3">
                {decision.state_transitions.map((t, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium text-gray-700 min-w-[120px]">{t.field_name}</span>
                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-sm line-through">
                      {String(t.previous_value)}
                    </span>
                    <ArrowRight className="w-4 h-4 text-gray-400" />
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-medium">
                      {String(t.new_value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Impact Assessment */}
          {decision.impact_assessment && Object.values(decision.impact_assessment).some(v => v) && (
            <div className="bg-white rounded-xl border p-5">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-gray-400" />
                Impact Assessment
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(decision.impact_assessment).filter(([_, v]) => v).map(([key, value]) => (
                  <div key={key} className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs font-medium text-gray-500 uppercase">{key.replace('_', ' ')}</p>
                    <p className="text-sm text-gray-900 mt-1">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Linkages */}
          <div className="bg-white rounded-xl border p-5">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <ExternalLink className="w-4 h-4 text-gray-400" />
              Related Entities
            </h3>
            {(decision.demand_id || decision.project_id || decision.program_id || decision.portfolio_id || decision.governance_item_id) ? (
              <div className="grid grid-cols-2 gap-3 text-sm">
                {decision.demand_id && (
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-blue-500" />
                    <span className="text-gray-500">Demand:</span>
                    <span className="font-mono text-blue-600">{decision.demand_id}</span>
                  </div>
                )}
                {decision.project_id && (
                  <div className="flex items-center gap-2">
                    <Folder className="w-4 h-4 text-indigo-500" />
                    <span className="text-gray-500">Project:</span>
                    <span className="font-mono text-indigo-600">{decision.project_id}</span>
                  </div>
                )}
                {decision.program_id && (
                  <div className="flex items-center gap-2">
                    <Building className="w-4 h-4 text-purple-500" />
                    <span className="text-gray-500">Program:</span>
                    <span className="font-mono text-purple-600">{decision.program_id}</span>
                  </div>
                )}
                {decision.portfolio_id && (
                  <div className="flex items-center gap-2">
                    <Building className="w-4 h-4 text-amber-500" />
                    <span className="text-gray-500">Portfolio:</span>
                    <span className="font-mono text-amber-600">{decision.portfolio_id}</span>
                  </div>
                )}
                {decision.governance_item_id && (
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-red-500" />
                    <span className="text-gray-500">Governance:</span>
                    <span className="font-mono text-red-600">{decision.governance_item_id}</span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">No related entities linked to this decision</p>
            )}
          </div>

          {/* Outcome */}
          {decision.outcome && (
            <div className="bg-white rounded-xl border p-5">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-gray-400" />
                Outcome
              </h3>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  decision.outcome.status === 'successful' ? 'bg-green-100 text-green-700' :
                  decision.outcome.status === 'failed' ? 'bg-red-100 text-red-700' :
                  decision.outcome.status === 'superseded' ? 'bg-amber-100 text-amber-700' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {decision.outcome.status}
                </span>
                {decision.outcome.measured_at && (
                  <span className="text-sm text-gray-500">
                    Measured: {new Date(decision.outcome.measured_at).toLocaleDateString()}
                  </span>
                )}
              </div>
              {decision.outcome.notes && (
                <p className="mt-3 text-sm text-gray-600">{decision.outcome.notes}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==========================================
// DECISION TABLE ROW
// ==========================================

const DecisionRow = ({ decision, onView, onVerify }) => {
  const [integrityStatus, setIntegrityStatus] = useState('pending');
  const categoryConfig = DECISION_CATEGORIES[decision.decision_category] || DECISION_CATEGORIES.general;
  
  const handleVerify = async (e) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API_BASE}/decision-ledger/${decision.decision_id}/verify`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setIntegrityStatus(data.valid ? 'verified' : 'failed');
    } catch {
      setIntegrityStatus('warning');
    }
  };

  return (
    <tr 
      className="hover:bg-gray-50 cursor-pointer transition-colors"
      onClick={() => onView(decision)}
      data-testid={`decision-row-${decision.decision_id}`}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-gray-500">{decision.decision_id}</span>
          <CopyButton text={decision.decision_id} />
        </div>
      </td>
      <td className="px-4 py-3">
        <CategoryBadge category={decision.decision_category} />
        <p className="text-sm font-medium text-gray-900 mt-1">
          {decision.decision_type?.replace(/_/g, ' ')}
        </p>
      </td>
      <td className="px-4 py-3">
        <p className="text-sm text-gray-500">
          {new Date(decision.timestamp).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          })}
        </p>
        <p className="text-xs text-gray-400">
          {new Date(decision.timestamp).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
          })}
        </p>
      </td>
      <td className="px-4 py-3">
        <MakerBadge type={decision.made_by} name={decision.made_by_name} />
      </td>
      <td className="px-4 py-3">
        <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
          {decision.entity_name}
        </p>
        <p className="text-xs text-gray-400">{decision.entity_type}</p>
      </td>
      <td className="px-4 py-3">
        <span className={`px-2 py-1 rounded text-xs font-medium ${
          decision.outcome?.status === 'successful' ? 'bg-green-100 text-green-700' :
          decision.outcome?.status === 'failed' ? 'bg-red-100 text-red-700' :
          decision.outcome?.status === 'superseded' ? 'bg-amber-100 text-amber-700' :
          'bg-gray-100 text-gray-600'
        }`}>
          {decision.outcome?.status || 'pending'}
        </span>
      </td>
      <td className="px-4 py-3">
        <IntegrityBadge 
          status={integrityStatus} 
          onClick={handleVerify}
        />
      </td>
      <td className="px-4 py-3">
        <button
          onClick={(e) => { e.stopPropagation(); onView(decision); }}
          className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-indigo-600"
        >
          <Eye className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
};

// ==========================================
// MAIN COMPONENT
// ==========================================

const DecisionLedgerPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [decisions, setDecisions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, skip: 0, limit: 25, has_more: false });
  
  // Filters
  const [filters, setFilters] = useState({
    decision_type: searchParams.get('type') || '',
    category: searchParams.get('category') || '',
    entity_type: searchParams.get('entity_type') || '',
    entity_id: searchParams.get('entity_id') || '',
    made_by: searchParams.get('made_by') || '',
    from_date: searchParams.get('from_date') || '',
    to_date: searchParams.get('to_date') || ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Views
  const [viewMode, setViewMode] = useState('table'); // table, timeline, executive
  const [selectedDecision, setSelectedDecision] = useState(null);
  
  // Decision types for filter dropdown
  const [decisionTypes, setDecisionTypes] = useState({});

  // Load decision types
  useEffect(() => {
    fetch(`${API_BASE}/decision-ledger/types`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => setDecisionTypes(data.types || {}))
      .catch(() => {});
  }, []);

  // Load decisions
  const loadDecisions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      params.append('limit', pagination.limit);
      params.append('skip', pagination.skip);

      const [decisionsRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/decision-ledger?${params}`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE}/decision-ledger/statistics`, { headers: getAuthHeaders() })
      ]);

      const decisionsData = await decisionsRes.json();
      const statsData = await statsRes.json();

      setDecisions(decisionsData.decisions || []);
      setPagination(prev => ({
        ...prev,
        total: decisionsData.total || 0,
        has_more: decisionsData.pagination?.has_more || false
      }));
      setStats(statsData);
    } catch (err) {
      toast.error('Failed to load decisions');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit, pagination.skip]);

  useEffect(() => {
    loadDecisions();
  }, [loadDecisions]);

  // Filter change handler
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, skip: 0 }));
    
    // Update URL params
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      decision_type: '',
      category: '',
      entity_type: '',
      entity_id: '',
      made_by: '',
      from_date: '',
      to_date: ''
    });
    setSearchParams({});
  };

  // Search filter
  const filteredDecisions = useMemo(() => {
    if (!searchQuery) return decisions;
    const q = searchQuery.toLowerCase();
    return decisions.filter(d =>
      d.decision_id?.toLowerCase().includes(q) ||
      d.entity_name?.toLowerCase().includes(q) ||
      d.made_by_name?.toLowerCase().includes(q) ||
      d.rationale?.toLowerCase().includes(q)
    );
  }, [decisions, searchQuery]);

  const hasActiveFilters = Object.values(filters).some(v => v);

  return (
    <div className="min-h-screen bg-gray-100" data-testid="decision-ledger-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-20">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <Shield className="w-7 h-7 text-indigo-600" />
                Decision Ledger
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Immutable record of governance decisions • Foundation for Decision Integrity
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {/* View Mode Toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'table' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="view-table-btn"
                >
                  Table
                </button>
                <button
                  onClick={() => setViewMode('timeline')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'timeline' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="view-timeline-btn"
                >
                  Timeline
                </button>
                <button
                  onClick={() => setViewMode('executive')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'executive' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="view-executive-btn"
                >
                  Executive
                </button>
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                  showFilters || hasActiveFilters ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'hover:bg-gray-50'
                }`}
                data-testid="toggle-filters-btn"
              >
                <Filter className="w-4 h-4" />
                Filters
                {hasActiveFilters && (
                  <span className="bg-indigo-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                    {Object.values(filters).filter(v => v).length}
                  </span>
                )}
              </button>

              <button
                onClick={loadDecisions}
                className="p-2 hover:bg-gray-100 rounded-lg"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-5 h-5 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mt-4 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by ID, entity, maker, or rationale..."
              className="w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              data-testid="search-input"
            />
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-4 p-4 bg-gray-50 rounded-xl border" data-testid="filters-panel">
              <div className="grid grid-cols-6 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Category</label>
                  <select
                    value={filters.category}
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    data-testid="filter-category"
                  >
                    <option value="">All Categories</option>
                    {Object.entries(DECISION_CATEGORIES).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Decision Type</label>
                  <select
                    value={filters.decision_type}
                    onChange={(e) => handleFilterChange('decision_type', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    data-testid="filter-type"
                  >
                    <option value="">All Types</option>
                    {Object.entries(decisionTypes).flatMap(([_, types]) =>
                      types.map(t => (
                        <option key={t.value} value={t.value}>{t.name}</option>
                      ))
                    )}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Entity Type</label>
                  <select
                    value={filters.entity_type}
                    onChange={(e) => handleFilterChange('entity_type', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    data-testid="filter-entity-type"
                  >
                    <option value="">All Entities</option>
                    <option value="demand">Demand</option>
                    <option value="project">Project</option>
                    <option value="program">Program</option>
                    <option value="portfolio">Portfolio</option>
                    <option value="escalation">Escalation</option>
                    <option value="risk">Risk</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Maker Type</label>
                  <select
                    value={filters.made_by}
                    onChange={(e) => handleFilterChange('made_by', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    data-testid="filter-maker"
                  >
                    <option value="">All Makers</option>
                    <option value="human">Human</option>
                    <option value="system">System</option>
                    <option value="agent">AI Agent</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">From Date</label>
                  <input
                    type="date"
                    value={filters.from_date}
                    onChange={(e) => handleFilterChange('from_date', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    data-testid="filter-from-date"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">To Date</label>
                  <input
                    type="date"
                    value={filters.to_date}
                    onChange={(e) => handleFilterChange('to_date', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    data-testid="filter-to-date"
                  />
                </div>
              </div>

              {hasActiveFilters && (
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-sm text-gray-500">
                    {pagination.total} decision{pagination.total !== 1 ? 's' : ''} found
                  </span>
                  <button
                    onClick={clearFilters}
                    className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                  >
                    Clear all filters
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        {loading && decisions.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
          </div>
        ) : viewMode === 'executive' ? (
          <ExecutiveView 
            stats={stats} 
            recentDecisions={decisions}
            onViewDecision={setSelectedDecision}
          />
        ) : viewMode === 'timeline' ? (
          <TimelineView 
            decisions={filteredDecisions}
            onViewDecision={setSelectedDecision}
          />
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <StatCard
                title="Total Decisions"
                value={stats?.total_decisions || 0}
                icon={Shield}
                color="indigo"
              />
              <StatCard
                title="Human Decisions"
                value={stats?.by_maker?.human || 0}
                icon={User}
                color="blue"
              />
              <StatCard
                title="System Automated"
                value={stats?.by_maker?.system || 0}
                icon={Zap}
                color="amber"
              />
              <StatCard
                title="Pending Outcomes"
                value={stats?.by_outcome?.pending || 0}
                icon={Clock}
                color="gray"
              />
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              {filteredDecisions.length === 0 ? (
                <div className="text-center py-16">
                  <Shield className="w-16 h-16 mx-auto text-gray-200 mb-4" />
                  <p className="text-gray-500 text-lg">No decisions found</p>
                  <p className="text-gray-400 text-sm mt-1">
                    {hasActiveFilters ? 'Try adjusting your filters' : 'Decisions will appear here as they are recorded'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">ID</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Type</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Decision Maker</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Entity</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Integrity</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {filteredDecisions.map(decision => (
                          <DecisionRow
                            key={decision.decision_id}
                            decision={decision}
                            onView={setSelectedDecision}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="px-4 py-3 border-t flex items-center justify-between bg-gray-50">
                    <span className="text-sm text-gray-500">
                      Showing {pagination.skip + 1}-{Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPagination(p => ({ ...p, skip: Math.max(0, p.skip - p.limit) }))}
                        disabled={pagination.skip === 0}
                        className="px-3 py-1 border rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setPagination(p => ({ ...p, skip: p.skip + p.limit }))}
                        disabled={!pagination.has_more}
                        className="px-3 py-1 border rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Decision Detail Drawer */}
      <DecisionDetailDrawer
        decision={selectedDecision}
        isOpen={!!selectedDecision}
        onClose={() => setSelectedDecision(null)}
      />
    </div>
  );
};

export default DecisionLedgerPage;
